'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { updateUserProperties } from '@/instrumentation-client'
import type { AuthChallengeMethod } from '@/server/domain/clients'
import {
  AuthLanguage,
  AuthOperationResult,
  FetchUserDataInput,
  User,
  deleteCurrentAccount,
  cacheLanguage,
  getCachedLanguage,
  logoutCurrentSession,
  requireAuthenticatedUser,
  sendOtpEmail,
  startAppleSignIn,
  startGoogleSignIn,
  verifyOtpCode
} from '@/contexts/auth-operations'
import { getAccount, putAccount } from '@/lib/account-api'
import type { AccountSnapshot } from '@/lib/account-snapshot'
import { fetchSynchronizedUser, type SyncInFlightRef } from '@/contexts/auth-account-sync'
import { useInitialSessionSync } from '@/contexts/auth-session-sync'
import { accountPersonProperties } from '@/lib/account-person-properties'

export interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  preferredLanguage: AuthLanguage
  updateLanguage: (language: AuthLanguage) => Promise<void>
  updateMarketingConsent: (consent: boolean) => Promise<void>
  updatePin: (pin: string) => Promise<void>
  refreshAccount: () => Promise<void>
  applyAccountSnapshot: (account: AccountSnapshot) => void
  signInWithApple: (locale?: string) => Promise<AuthOperationResult>
  signInWithGoogle: (locale?: string) => Promise<AuthOperationResult>
  sendOtp: (email: string, locale?: string) => Promise<AuthOperationResult>
  verifyOtp: (email: string, code: string, locale?: string, verificationMethod?: AuthChallengeMethod) => Promise<AuthOperationResult>
  logout: () => Promise<void>
  deleteAccount: () => Promise<{ success: boolean; error?: string }>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // Solo para la sesión cerrada: con sesión, el idioma sale de `user`, así que
  // no puede desincronizarse ni sobrevivir a un logout.
  const [guestLanguage, setGuestLanguage] = useState<AuthLanguage>(() => getCachedLanguage() || 'es')
  const preferredLanguage = user?.preferredLanguage ?? guestLanguage
  const syncInFlightRef = useRef<SyncInFlightRef['current']>(null)

  const handlers = useMemo(() => ({
    setUser,
    setGuestLanguage,
    setIsLoading
  }), [])

  // La caché del idioma (y `<html lang>`) sale de `user`: una respuesta que
  // llega tras cerrar la sesión no puede volver a escribirla.
  useEffect(() => {
    if (user) {
      updateUserProperties(accountPersonProperties(user))
      cacheLanguage(user.preferredLanguage)
    }
  }, [user])

  const fetchUserData = useCallback((input: FetchUserDataInput): Promise<AuthOperationResult> => {
    return fetchSynchronizedUser({
      ...input,
      syncInFlightRef,
      handlers
    })
  }, [handlers])

  useInitialSessionSync({ handlers, syncInFlightRef })

  const applyAccountSnapshot = useCallback((account: AccountSnapshot): void => {
    setUser((currentUser) => currentUser
      ? { ...currentUser, ...account }
      : currentUser
    )
  }, [])
  // Toda lectura o cambio de cuenta refleja lo que el servidor confirma, no lo
  // que se pidió: la regla vive aquí y no en cada operación.
  const commitAccount = async (action: string, request: () => Promise<AccountSnapshot>): Promise<void> => {
    requireAuthenticatedUser(user, action)
    applyAccountSnapshot(await request())
  }
  const updateLanguage = (language: AuthLanguage): Promise<void> =>
    commitAccount('update language', () => putAccount({ preferredLanguage: language }))
  const updateMarketingConsent = (consent: boolean): Promise<void> =>
    commitAccount('update marketing consent', () => putAccount({ marketingConsent: consent }))
  const updatePin = (pin: string): Promise<void> =>
    commitAccount('update PIN', () => putAccount({ pin }))
  const refreshAccount = (): Promise<void> => commitAccount('refresh account', getAccount)
  const signInWithGoogle = (locale?: string): Promise<AuthOperationResult> => startGoogleSignIn({
    locale,
    fetchUserData
  })
  const signInWithApple = (locale?: string): Promise<AuthOperationResult> => startAppleSignIn({
    locale,
    fetchUserData
  })
  const sendOtp = (email: string, locale?: string): Promise<AuthOperationResult> => sendOtpEmail(email, locale)
  const verifyOtp = (email: string, code: string, locale?: string, verificationMethod?: AuthChallengeMethod): Promise<AuthOperationResult> => verifyOtpCode({
    email,
    code,
    locale,
    verificationMethod,
    fetchUserData
  })
  const logout = (): Promise<void> => logoutCurrentSession(handlers)
  const deleteAccount = (): Promise<{ success: boolean; error?: string }> => deleteCurrentAccount(handlers)

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        preferredLanguage,
        updateLanguage,
        updateMarketingConsent,
        updatePin,
        refreshAccount,
        applyAccountSnapshot,
        signInWithApple,
        signInWithGoogle,
        sendOtp,
        verifyOtp,
        logout,
        deleteAccount
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
